// ============================================================
// sunTimes.ts — UK (London) sunrise / sunset, worked out locally.
// The NOAA solar calculator's equations (equation of time + solar
// declination, refraction-corrected zenith 90.833°), refined once at the
// event itself. No location permission, no external API. Accurate to about
// a minute for London; good enough for "dark from sunset to sunrise".
// Results are INSTANTS (Date), so BST / GMT only matters when showing them
// (ukTime below formats in Europe/London).
// ============================================================

export const LONDON = { lat: 51.5074, lng: -0.1278 }

const rad = (d: number): number => (d * Math.PI) / 180
const deg = (r: number): number => (r * 180) / Math.PI

// Declination (°) and equation of time (minutes) at a Julian century.
const solar = (jc: number): { decl: number; eqTime: number } => {
  const L0 = (280.46646 + jc * (36000.76983 + jc * 0.0003032)) % 360
  const M = 357.52911 + jc * (35999.05029 - 0.0001537 * jc)
  const e = 0.016708634 - jc * (0.000042037 + 0.0000001267 * jc)
  const C = Math.sin(rad(M)) * (1.914602 - jc * (0.004817 + 0.000014 * jc)) + Math.sin(rad(2 * M)) * (0.019993 - 0.000101 * jc) + Math.sin(rad(3 * M)) * 0.000289
  const omega = 125.04 - 1934.136 * jc
  const lambda = L0 + C - 0.00569 - 0.00478 * Math.sin(rad(omega))
  const eps0 = 23 + (26 + (21.448 - jc * (46.815 + jc * (0.00059 - jc * 0.001813))) / 60) / 60
  const eps = eps0 + 0.00256 * Math.cos(rad(omega))
  const decl = deg(Math.asin(Math.sin(rad(eps)) * Math.sin(rad(lambda))))
  const y = Math.tan(rad(eps / 2)) ** 2
  const eqTime = 4 * deg(y * Math.sin(2 * rad(L0)) - 2 * e * Math.sin(rad(M)) + 4 * e * y * Math.sin(rad(M)) * Math.cos(2 * rad(L0))
    - 0.5 * y * y * Math.sin(4 * rad(L0)) - 1.25 * e * e * Math.sin(2 * rad(M)))
  return { decl, eqTime }
}

// Minutes after 00:00 UTC of the event on that UTC day (sign +1 = sunrise, −1 = sunset).
const eventMinutes = (dayUtcMs: number, sign: 1 | -1, lat: number, lng: number): number | null => {
  const jd0 = dayUtcMs / 86400000 + 2440587.5
  let t = 720
  for (let i = 0; i < 3; i++) {
    const { decl, eqTime } = solar((jd0 + t / 1440 - 2451545) / 36525)
    const cosHa = Math.cos(rad(90.833)) / (Math.cos(rad(lat)) * Math.cos(rad(decl))) - Math.tan(rad(lat)) * Math.tan(rad(decl))
    if (cosHa > 1 || cosHa < -1) return null // no sunrise / sunset that day (never in the UK)
    t = 720 - 4 * (lng + sign * deg(Math.acos(cosHa))) - eqTime
  }
  return t
}

// Sunrise and sunset on a calendar day (y, m 1–12, d) at London.
export const sunTimes = (y: number, m: number, d: number, at = LONDON): { sunrise: Date; sunset: Date } => {
  const day = Date.UTC(y, m - 1, d)
  const rise = eventMinutes(day, 1, at.lat, at.lng)
  const set = eventMinutes(day, -1, at.lat, at.lng)
  return { sunrise: new Date(day + (rise ?? 360) * 60000), sunset: new Date(day + (set ?? 1080) * 60000) }
}

// ── Europe/London wall clock ─────────────────────────────────
const UK_PARTS = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
export const ukParts = (at: Date): { y: number; m: number; d: number; minutes: number } => {
  const p = Object.fromEntries(UK_PARTS.formatToParts(at).map((x) => [x.type, x.value]))
  return { y: +p.year, m: +p.month, d: +p.day, minutes: (+p.hour % 24) * 60 + +p.minute }
}
// The instant a UK wall-clock time happens (BST handled: the offset is read
// back from Intl and corrected once).
export const ukInstant = (y: number, m: number, d: number, minutes: number): Date => {
  const target = Date.UTC(y, m - 1, d) + minutes * 60000
  let guess = target
  for (let i = 0; i < 2; i++) {
    const p = ukParts(new Date(guess))
    const seen = Date.UTC(p.y, p.m - 1, p.d) + p.minutes * 60000
    guess += target - seen
  }
  return new Date(guess)
}
// "16:53" in UK time.
export const ukTime = (at: Date): string => {
  const { minutes } = ukParts(at)
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}
